"use client";

import { useRef, useState, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { getImageUrl } from "@/utils/api-utils";
import type { HistoryGalleryItem } from "./types";

interface HistoryGallerySliderProps {
  items: HistoryGalleryItem[];
  /** Khi set: render nav vào element này (vd. header), không render nav dưới grid */
  navSlotRef?: React.RefObject<HTMLDivElement | null>;
}

export default function HistoryGallerySlider({ items, navSlotRef }: HistoryGallerySliderProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [navSlotNode, setNavSlotNode] = useState<HTMLDivElement | null>(null);
  const total = items.length;

  const scrollByCard = useCallback(
    (direction: "prev" | "next") => {
      const grid = gridRef.current;
      if (!grid || total === 0) return;
      const firstItem = grid.querySelector("[data-history-index]") as HTMLElement | null;
      const gapPx = parseFloat(getComputedStyle(grid).gap) || 0;
      const step = firstItem ? firstItem.offsetWidth + gapPx : grid.clientWidth;
      grid.scrollBy({
        left: direction === "next" ? step : -step,
        behavior: "smooth",
      });
    },
    [total]
  );

  const goPrev = useCallback(() => {
    scrollByCard("prev");
  }, [scrollByCard]);

  const goNext = useCallback(() => {
    scrollByCard("next");
  }, [scrollByCard]);

  const canGoPrev = activeIndex > 0;
  const canGoNext = activeIndex < total - 1;

  const handleScroll = useCallback(() => {
    const grid = gridRef.current;
    if (!grid || total === 0) return;
    const scrollLeft = grid.scrollLeft;
    const scrollWidth = grid.scrollWidth;
    const clientWidth = grid.clientWidth;
    const threshold = 10;
    const atStart = scrollLeft <= threshold;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - threshold;
    if (atEnd) {
      setActiveIndex(total - 1);
      return;
    }
    if (atStart) {
      setActiveIndex(0);
      return;
    }
    const itemEls = grid.querySelectorAll("[data-history-index]");
    let current = 0;
    let minDistance = Infinity;
    itemEls.forEach((el, i) => {
      const left = (el as HTMLElement).offsetLeft;
      const distance = Math.abs(left - scrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        current = i;
      }
    });
    setActiveIndex(current);
  }, [total]);

  // Portal nav vào header slot khi có navSlotRef. Dùng useLayoutEffect + state để tránh đọc ref trong render.
  // Chạy lại frame tiếp theo để bắt ref.current sau khi parent mount div.
  useLayoutEffect(() => {
    let cancelled = false;
    const sync = () => {
      if (cancelled) return;
      const node = navSlotRef?.current ?? null;
      setNavSlotNode(node);
    };
    sync();
    const rafId = requestAnimationFrame(sync);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      setNavSlotNode(null);
    };
  }, [navSlotRef]);

  const navContent = (
    <nav
      className="history-slider-nav history-gallery__header-nav"
      aria-label="Điều hướng slider lịch sử"
    >
      <button
        type="button"
        className="carousel-nav carousel-nav--prev"
        aria-label="Slide trước"
        onClick={goPrev}
        disabled={!canGoPrev}
      >
        <Image
          src="/assets/svg/arrow-left.svg"
          alt=""
          width={25}
          height={25}
          loading="lazy"
        />
      </button>
      <button
        type="button"
        className="carousel-nav carousel-nav--next"
        aria-label="Slide sau"
        onClick={goNext}
        disabled={!canGoNext}
      >
        <Image
          src="/assets/svg/arrow-right.svg"
          alt=""
          width={25}
          height={25}
          loading="lazy"
        />
      </button>
    </nav>
  );

  const renderNavInSlot = Boolean(navSlotNode);

  return (
    <>
      {renderNavInSlot && navSlotNode && createPortal(navContent, navSlotNode)}
      <div className="history-gallery__slider-wrap">
        <div
          ref={gridRef}
          className="history-gallery__grid"
          onScroll={handleScroll}
          role="region"
          aria-label="Gallery lịch sử hình thành"
        >
          {items.map((item, index) => (
            <div
              key={item.id}
              className="history-gallery__item"
              data-history-index={index}
            >
              <div className="history-gallery__image">
                <Image
                  src={getImageUrl(item.image)}
                  alt={item.caption}
                  width={365}
                  height={248}
                  loading="lazy"
                />
              </div>
              <p className="history-gallery__caption">{item.caption}</p>
              <span className="history-gallery__year">{item.year}</span>
              <div className="history-gallery__divider" />
              <p className="history-gallery__description">{item.description}</p>
            </div>
          ))}
        </div>

        {!navSlotRef && (
          <nav
            className="history-slider-nav"
            aria-label="Điều hướng slider lịch sử"
          >
            <button
              type="button"
              className="history-slider-nav__btn history-slider-nav__btn--prev"
              aria-label="Slide trước"
              onClick={goPrev}
              disabled={!canGoPrev}
            >
              <Image
                src="/assets/svg/d-arrow-left.svg"
                alt=""
                width={11}
                height={10}
                loading="lazy"
                className="history-slider-nav__icon history-slider-nav__icon--prev"
              />
            </button>
            <button
              type="button"
              className="history-slider-nav__btn history-slider-nav__btn--next"
              aria-label="Slide sau"
              onClick={goNext}
              disabled={!canGoNext}
            >
              <Image
                src="/assets/svg/d-arrow-right.svg"
                alt=""
                width={11}
                height={10}
                loading="lazy"
                className="history-slider-nav__icon"
              />
            </button>
          </nav>
        )}
      </div>
    </>
  );
}
